import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AccessReportCard } from "@/components/admin/AccessReportCard";
import { BetaInvitesCard, BastionReadinessCard, HipaaDocsCard } from "@/components/admin/LaunchCards";
import { SamplesCard } from "@/components/admin/SamplesCard";
import { ReadinessCard } from "@/components/admin/ReadinessCard";
import { DataSafetyCard } from "@/components/admin/DataSafetyCard";
import { MilestonesBoard } from "@/components/admin/MilestonesBoard";
import { toast } from "sonner";
import { Users, Activity, MessageSquare, Award, RefreshCw, Download, Send, KeyRound, TrendingUp, CreditCard } from "lucide-react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—");
const MS_PER_DAY = 86_400_000;
const daysAgo = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / MS_PER_DAY) : null);

function Stat({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="warm-card p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-stone-500 text-[10px] font-bold uppercase tracking-widest"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className="font-mono text-3xl font-bold text-amber-800 mt-1">{value}</div>
      {sub && <div className="text-xs text-stone-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function BetaBoard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [billing, setBilling] = useState(null);

  const load = () => Promise.all([api.get("/admin/beta-stats"), api.get("/admin/billing-mode")])
    .then(([r, b]) => { setData(r.data); setBilling(b.data); })
    .catch(() => setErr(true));
  useEffect(() => { load(); }, []);

  const switchBilling = async (mode) => {
    const msg = mode === "trial"
      ? `Open billing now?\n\n• Every existing account gets a fresh ${billing.trial_days}-day free trial starting today.\n• After that, solo members see the $23.88/yr or $4.99/mo plans; clinicians see $1/client/month + $25 setup; linked clients stay covered.\n• Accounts that don't subscribe become read-only (history stays visible).\n\nYou can switch back to free at any time.`
      : "Switch back to free beta mode? Paywalls and trial banners disappear immediately; existing subscriptions keep running in Stripe.";
    if (!window.confirm(msg)) return;
    try {
      await api.post("/admin/billing-mode", { mode });
      toast.success(mode === "trial" ? "Billing is open. Trials started for everyone today." : "Back to free beta mode.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const nudge = async (t) => {
    try {
      const r = await api.post(`/admin/nudge/${t.id}`, {});
      toast.success(`Nudge sent to ${t.name}${r.data.emailed ? " (in-app + email)" : " (in-app)"}.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const resetLink = async (t) => {
    try {
      const r = await api.post(`/admin/reset-link/${t.id}`);
      await navigator.clipboard.writeText(r.data.link);
      toast.success(`Reset link for ${t.name} copied — valid 60 minutes. Send it to them directly.`, { duration: 8000 });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const exportCsv = async () => {
    const r = await api.get("/admin/feedback.csv", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `root-and-rose-feedback-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (user && !user.is_admin) return <Navigate to="/" replace />;
  if (err) return <div className="max-w-5xl mx-auto px-4 py-12 text-stone-500">This page is only for the app owner.</div>;
  if (!data) return <div className="max-w-5xl mx-auto px-4 py-12 text-stone-500">Loading beta board…</div>;

  const clients = data.testers.filter((t) => t.role === "client");
  const clinicians = data.testers.filter((t) => t.role === "coach");

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10 space-y-8 relative z-10" data-testid="beta-board">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-amber-800">Private · owner only</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-stone-900 mt-1">Beta progress board</h1>
          <p className="text-sm text-stone-500 mt-1">Updated {new Date(data.generated_at).toLocaleString()}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={async () => { try { const r = await api.post("/admin/beta-digest/send"); toast.success(`Beta digest emailed to ${r.data.sent_to}`); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }} data-testid="beta-digest-send"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-stone-300 text-stone-700 hover:border-amber-600 hover:text-amber-800" title="Also sent automatically every Monday at 8 am"><Send className="w-3.5 h-3.5" /> Email me the weekly digest</button>
          <button onClick={load} data-testid="beta-refresh" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-stone-900 text-white hover:bg-amber-700"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
        </div>
      </div>

      {billing && (
        <div className={`rounded-3xl p-5 border flex items-start justify-between gap-4 flex-wrap ${billing.mode === "free" ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-300"}`} data-testid="billing-switch">
          <div className="flex items-start gap-3">
            <CreditCard className={`w-5 h-5 mt-0.5 ${billing.mode === "free" ? "text-emerald-700" : "text-amber-800"}`} />
            <div>
              <div className="font-display font-bold text-stone-900">
                Billing: <span data-testid="billing-mode-label">{billing.mode === "free" ? "Free beta" : "Trial & paid plans open"}</span>
              </div>
              <p className="text-xs text-stone-600 mt-0.5 max-w-xl">
                {billing.mode === "free"
                  ? `Everyone has full access at no cost. When beta ends, open billing here: all ${billing.clients_without_clinician} solo members and ${billing.clinicians} coaches get a ${billing.trial_days}-day trial from that moment, then see plans.`
                  : "Trials are running. Accounts that don't subscribe after their trial become read-only. Switch back to free any time."}
              </p>
            </div>
          </div>
          <button
            data-testid="billing-switch-btn"
            onClick={() => switchBilling(billing.mode === "free" ? "trial" : "free")}
            className={`text-xs font-semibold px-4 py-2 rounded-full ${billing.mode === "free" ? "bg-stone-900 text-white hover:bg-amber-700" : "bg-white border border-stone-300 text-stone-800 hover:bg-stone-100"}`}
          >
            {billing.mode === "free" ? "Open billing (end beta)" : "Back to free beta"}
          </button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} label="Signed up" value={data.signups.total} sub={`${data.signups.clients} members · ${data.signups.clinicians} coaches · +${data.signups.this_week} this week`} testId="stat-signups" />
        <Stat icon={Activity} label="Active this week" value={data.active_this_week} sub={`of ${data.signups.clients} members checked in`} testId="stat-active" />
        <Stat icon={Activity} label="Check-ins this week" value={data.checkins_this_week.habits + data.checkins_this_week.wellbeing} sub={`${data.checkins_this_week.habits} habit · ${data.checkins_this_week.wellbeing} wellbeing`} testId="stat-checkins" />
        <Stat icon={MessageSquare} label="Feedback" value={data.feedback.total} sub={`+${data.feedback.this_week} this week · ${data.feedback.by_kind.bug} bugs · ${data.feedback.by_kind.idea} ideas · ${data.pioneers} pioneers`} testId="stat-feedback" />
      </div>

      <div className="warm-card p-6" data-testid="beta-trends">
        <div className="flex items-center gap-2 mb-3"><TrendingUp className="w-4 h-4 text-emerald-700" /><h2 className="font-display text-lg font-bold text-stone-900">Last 8 weeks</h2></div>
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={data.weekly}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E8E2D8" />
            <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#78716C" }} />
            <YAxis yAxisId="l" tick={{ fontSize: 11, fill: "#78716C" }} allowDecimals={false} />
            <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: "#78716C" }} allowDecimals={false} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar yAxisId="l" dataKey="habit_checkins" name="Habit check-ins" stackId="c" fill="#d9467a" radius={[0, 0, 0, 0]} />
            <Bar yAxisId="l" dataKey="wellbeing_checkins" name="Wellbeing check-ins" stackId="c" fill="#059669" radius={[4, 4, 0, 0]} />
            <Line yAxisId="r" type="monotone" dataKey="signups" name="Signups" stroke="#1C1917" strokeWidth={2} dot={{ r: 3 }} />
            <Line yAxisId="r" type="monotone" dataKey="feedback" name="Feedback" stroke="#7C3AED" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="4 3" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <DataSafetyCard />
      <SamplesCard />
      <ReadinessCard />
      <HipaaDocsCard />
      <BetaInvitesCard />

      <MilestonesBoard />
      <BastionReadinessCard />
      <AccessReportCard />

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 warm-card p-6">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <h2 className="font-display text-lg font-bold text-stone-900">Testers</h2>
            {data.quiet_count > 0 && <span className="text-xs font-semibold text-amber-800 bg-amber-100 px-2.5 py-1 rounded-full" data-testid="quiet-count">{data.quiet_count} quiet for 5+ days</span>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="testers-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-stone-500">
                  <th className="pb-2 pr-3">Name</th><th className="pb-2 pr-3">Role</th><th className="pb-2 pr-3">Joined</th><th className="pb-2 pr-3">Last check-in</th><th className="pb-2 pr-3 text-right">Wk check-ins</th><th className="pb-2 pr-3 text-right">Feedback</th><th className="pb-2"></th>
                </tr>
              </thead>
              <tbody>
                {[...clients, ...clinicians].map((t) => (
                  <tr key={t.id} className={`border-t border-stone-100 ${t.quiet ? "bg-amber-50/50" : ""}`} data-testid={`tester-${t.id}`}>
                    <td className="py-2 pr-3">
                      <div className="font-semibold text-stone-800 flex items-center gap-1.5">{t.name}{t.linked && <span className="text-[9px] font-bold uppercase tracking-widest text-emerald-800">linked</span>}{t.quiet && <span className="text-[9px] font-bold uppercase tracking-widest text-amber-800">quiet</span>}</div>
                      <div className="text-xs text-stone-400">{t.email}</div>
                    </td>
                    <td className="py-2 pr-3 text-stone-600">{t.role}</td>
                    <td className="py-2 pr-3 text-stone-600">{fmt(t.joined)}</td>
                    <td className="py-2 pr-3 text-stone-600">{t.role === "client" ? (t.last_checkin ? `${fmt(t.last_checkin)} (${daysAgo(t.last_checkin)}d)` : "never") : fmt(t.last_active)}</td>
                    <td className="py-2 pr-3 text-right font-mono">{t.role === "client" ? `${t.habit_checkins_week} · ${t.wellbeing_checkins_week}` : "—"}</td>
                    <td className="py-2 pr-3 text-right font-mono">{t.feedback_count || ""}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      {t.quiet && (
                        <button
                          data-testid={`nudge-${t.id}`}
                          onClick={() => nudge(t)}
                          disabled={t.last_nudged_at && daysAgo(t.last_nudged_at) < 3}
                          title={t.last_nudged_at ? `Nudged ${daysAgo(t.last_nudged_at)}d ago` : "Send a friendly how's-it-going note"}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-600 text-white hover:bg-amber-700 disabled:bg-stone-200 disabled:text-stone-500"
                        >
                          <Send className="w-3 h-3" /> {t.last_nudged_at && daysAgo(t.last_nudged_at) < 3 ? "Nudged" : "Nudge"}
                        </button>
                      )}
                      <button data-testid={`reset-link-${t.id}`} onClick={() => resetLink(t)} title="Copy a one-time password reset link to send them" className="ml-1 inline-flex items-center text-stone-400 hover:text-amber-800 p-1"><KeyRound className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[11px] text-stone-400 mt-2">Wk check-ins = habit · wellbeing, last 7 days. Quiet = no check-in for 5+ days. Key icon copies a 60-minute password reset link for that tester.</div>
        </div>

        <div className="lg:col-span-2 warm-card p-6">
          <div className="flex items-center justify-between gap-2 mb-3">
            <h2 className="font-display text-lg font-bold text-stone-900">Latest feedback</h2>
            <button data-testid="export-feedback-csv" onClick={exportCsv} className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 hover:text-amber-900"><Download className="w-3.5 h-3.5" /> Export CSV</button>
          </div>
          {data.feedback.items.length === 0 ? (
            <p className="text-sm text-stone-500">Nothing yet — testers use the Beta feedback button bottom-left.</p>
          ) : (
            <ul className="space-y-3 max-h-[560px] overflow-y-auto pr-1" data-testid="feedback-list">
              {data.feedback.items.map((f) => (
                <li key={f.id} className="rounded-2xl bg-stone-50 p-3" data-testid={`feedback-${f.id}`}>
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-widest font-bold">
                    <span className={f.kind === "bug" ? "text-red-700" : f.kind === "idea" ? "text-emerald-800" : "text-stone-500"}>{f.kind}</span>
                    <span className="text-stone-400">{fmt(f.created_at)} · {f.page}</span>
                  </div>
                  <p className="text-sm text-stone-800 mt-1 whitespace-pre-wrap">{f.message}</p>
                  <div className="text-xs text-stone-500 mt-1">{f.name} · {f.email}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className="text-xs text-stone-500 flex items-center gap-2"><Award className="w-3.5 h-3.5 text-amber-600" /> Pioneers = testers who checked in on 7 different days and received the Beta Pioneer thank-you.</div>
    </div>
  );
}
